import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ApplicationButton,
  ApplicationContextualHelp,
  ApplicationPage,
  ApplicationStateRegion,
  ApplicationViewHeader,
} from "../../../components/application";
import ActivitySemanticSetup from "../components/ActivitySemanticSetup";
import { createActivityVersion, duplicateActivity, getAuthoringActivity } from "../services/activityAuthoringApi";

export default function ActivityDetailPage() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionState, setActionState] = useState("idle");

  useEffect(() => {
    getAuthoringActivity(activityId).then(setActivity).catch(() => setError("This activity could not be loaded."));
  }, [activityId]);

  if (error) return <ApplicationPage variant="workspace"><ApplicationStateRegion status="error" message={error} /></ApplicationPage>;
  if (!activity) return <ApplicationPage variant="workspace"><ApplicationStateRegion status="loading" message="Loading activity…" /></ApplicationPage>;
  const personal = activity.visibility === "only_you";
  const ready = activity.lifecycle === "ready_to_assign" || activity.lifecycle === "system_ready";

  async function createDraft(action) {
    try {
      setActionError("");
      setActionState(action);
      const draft = action === "version" ? await createActivityVersion(activity.id) : await duplicateActivity(activity.id);
      navigate(`/app/facilitate/activities/${draft.id}/edit`);
    } catch {
      setActionError("The new draft could not be created. This ready activity remains unchanged.");
      setActionState("idle");
    }
  }

  return (
    <ApplicationPage className="av-activity-detail" variant="workspace">
      <ApplicationViewHeader
        contextLabel="Facilitate · Activities"
        title={activity.name}
        description={activity.description || (!personal ? "Ready-made activity provided by All-Voiced." : undefined)}
        backNavigation={<ApplicationButton variant="quiet" onClick={() => navigate("/app/facilitate/activities")}>Back to activities</ApplicationButton>}
        metadata={<><strong>{ready ? "Ready to assign" : "Draft"}</strong>{personal && activity.lineage?.version ? <span>Version {activity.lineage.version}</span> : null}{personal ? <span>Private</span> : <span>Provided by All-Voiced</span>} {ready ? <span className="av-activity-detail__immutable">Immutable</span> : null}</>}
      />
      {personal && ready ? <div className="av-activity-detail__actions" aria-label="Activity actions">
        <span><ApplicationButton onClick={() => createDraft("version")} disabled={actionState !== "idle"}>{actionState === "version" ? "Creating version…" : "Create new version"}</ApplicationButton><ApplicationContextualHelp label="About creating a new version" title="Create new version"><p>Creates a new editable version of this same activity. The current version remains unchanged and can still be used by existing assignments.</p></ApplicationContextualHelp></span>
        <span><ApplicationButton variant="secondary" onClick={() => createDraft("duplicate")} disabled={actionState !== "idle"}>{actionState === "duplicate" ? "Duplicating…" : "Duplicate activity"}</ApplicationButton><ApplicationContextualHelp label="About duplicating this activity" title="Duplicate activity"><p>Creates a separate activity using this one as a starting point. The duplicate has its own independent version history.</p></ApplicationContextualHelp></span>
      </div> : null}
      {actionError ? <ApplicationStateRegion status="error" message={actionError} /> : null}
      <ActivitySemanticSetup activity={activity} />
      {ready ? <p className="av-activity-detail__immutability-note">This activity is immutable. Future behavior changes require creating a new version.</p> : null}
    </ApplicationPage>
  );
}
